import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { THEME, gradients, shadows } from '../constants/theme';

const Documentation = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      const scrollPosition = window.scrollY + 100;

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(section.getAttribute('id'));
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: '🚀' },
    { id: 'authentication', title: 'Authentication & Security', icon: '🔐' },
    { id: 'business-profile', title: 'Business Profile', icon: '💼' },
    { id: 'clients', title: 'Client Management', icon: '👥' },
    { id: 'quotes', title: 'Quote Management', icon: '📝' },
    { id: 'invoices', title: 'Invoice Management', icon: '📊' },
    { id: 'payments', title: 'Payment Processing', icon: '💳' },
    { id: 'expenses', title: 'Expense Tracking', icon: '📱' },
    { id: 'service-desk', title: 'Service Desk', icon: '🎯' },
    { id: 'mobile-app', title: 'Mobile App', icon: '📱' },
    { id: 'ai-features', title: 'AI Features', icon: '🤖' },
    { id: 'analytics', title: 'Analytics', icon: '📈' },
    { id: 'subscriptions', title: 'Subscriptions', icon: '⭐️' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.background }}>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 right-4 z-50">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-lg shadow-lg hover:bg-opacity-90"
          style={{ backgroundColor: THEME.cardbg }}
        >
          {isSidebarOpen ? (
            <XMarkIcon className="h-6 w-6" style={{ color: THEME.primary }} />
          ) : (
            <Bars3Icon className="h-6 w-6" style={{ color: THEME.primary }} />
          )}
        </button>
      </div>

      {/* Sidebar Navigation */}
      <div className={`
        fixed top-0 left-0 h-full w-64 transform transition-transform duration-300 ease-in-out z-40
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}
      style={{ backgroundColor: THEME.cardbg, boxShadow: shadows.lg }}>
        <div className="p-6" style={{ background: gradients.primary }}>
          <Link to="/" className="flex items-center space-x-2">
            <img src="/gradient_logo.png" alt="Aided" className="h-8 w-8" />
            <span className="text-xl font-bold text-white">Aided</span>
          </Link>
        </div>
        <nav className="px-4 pb-6 overflow-y-auto h-[calc(100vh-88px)]">
          <div className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  scrollToSection(section.id);
                  if (window.innerWidth < 1024) setIsSidebarOpen(false);
                }}
                className={`
                  w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ease-in-out
                  flex items-center space-x-2
                `}
                style={{
                  backgroundColor: activeSection === section.id ? THEME.primaryLight + '20' : 'transparent',
                  color: activeSection === section.id ? THEME.primary : THEME.text
                }}
              >
                <span className="text-lg">{section.icon}</span>
                <ChevronRightIcon 
                  className={`h-4 w-4 transition-transform duration-200 ${
                    activeSection === section.id ? 'transform rotate-90' : ''
                  }`}
                />
                <span>{section.title}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className={`
        transition-all duration-300 ease-in-out
        ${isSidebarOpen ? 'lg:pl-64' : ''}
      `}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Hero Section */}
          <div className="rounded-2xl shadow-xl mb-8 p-8 text-white" style={{ background: gradients.primary }}>
            <h1 className="text-4xl font-bold mb-4">Welcome to Aided!</h1>
            <p className="text-lg opacity-90 mb-6">
              Your one-stop solution for managing business administration. Whether you're a freelancer, 
              small business owner, or large enterprise, Aided helps streamline your workflow and improve efficiency.
            </p>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4">Key Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: '🤖', title: 'AI-Powered', desc: 'Smart expense tracking & communication' },
                  { icon: '📊', title: 'Analytics', desc: 'Real-time business insights' },
                  { icon: '📱', title: 'Mobile Ready', desc: 'Manage business on the go' },
                  { icon: '🔐', title: 'Secure', desc: 'Enterprise-grade security' },
                  { icon: '⚡️', title: 'Automated', desc: 'Streamlined workflows' },
                  { icon: '🌐', title: 'Connected', desc: 'Integrated platform' },
                ].map((feature) => (
                  <div key={feature.title} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-white/20 transition-colors">
                    <div className="text-2xl">{feature.icon}</div>
                    <div>
                      <h4 className="font-medium">{feature.title}</h4>
                      <p className="text-sm opacity-90">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Documentation Sections */}
          <div className="rounded-2xl shadow-xl p-8" style={{ backgroundColor: THEME.cardbg }}>
            <div className="prose prose-lg max-w-none">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-20 mb-16">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: THEME.primaryLight + '20' }}>
                      <span className="text-2xl">{section.icon}</span>
                    </div>
                    <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>{section.title}</h2>
                  </div>
                  
                  {/* Section content from Aided context.md */}
                  <div style={{ color: THEME.text }}>
                    {/* Add section content here based on the section.id */}
                    {section.id === 'getting-started' && (
                      <>
                        <p className="mb-4">
                          When you first sign up for Aided, you'll be guided through a simple onboarding process:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 mb-6">
                          <li>Welcome Screen: Get an overview of Aided's features and benefits</li>
                          <li>Notification Preferences: Choose how you want to receive important updates</li>
                          <li>Business Information: Provide essential details about your business</li>
                          <li>Address Validation: Integrate with Google Maps to ensure accurate address information</li>
                        </ul>
                        [PLACEHOLDER for onboarding flow screenshots]
                      </>
                    )}
                    
                    {section.id === 'authentication' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">User Authentication</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Strong password requirements for enhanced security</li>
                              <li>Two-factor authentication (2FA) support</li>
                              <li>Secure password reset with email verification</li>
                              <li>Automatic session timeout protection</li>
                              <li>Device-based authentication tracking</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Data Security</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Row-level security with Supabase</li>
                              <li>End-to-end encryption for sensitive data</li>
                              <li>Rate-limited API endpoints</li>
                              <li>Regular security audits and updates</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for authentication screens]
                      </>
                    )}
                    
                    {section.id === 'business-profile' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Profile Information</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Business name and logo customization</li>
                              <li>Verified contact information</li>
                              <li>Google Maps integration for address validation</li>
                              <li>Tax and registration details management</li>
                              <li>Multi-currency support</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Customization</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Custom document templates</li>
                              <li>Brand color themes</li>
                              <li>Email notification preferences</li>
                              <li>Default terms and conditions</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for profile customization screens]
                      </>
                    )}
                    
                    {section.id === 'clients' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Client Management</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Comprehensive client information tracking</li>
                              <li>Multiple contact person support</li>
                              <li>Custom payment terms per client</li>
                              <li>Document storage and organization</li>
                              <li>Client activity history</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Client Portal</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Secure document access</li>
                              <li>Real-time communication</li>
                              <li>Payment tracking</li>
                              <li>Quote and invoice approval workflow</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for client management screens]
                      </>
                    )}
                    
                    {section.id === 'quotes' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Quote Creation</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Dynamic line item management</li>
                              <li>Multiple tax rate support</li>
                              <li>Custom templates</li>
                              <li>Automatic numbering</li>
                              <li>Draft and preview functionality</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Collaboration</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>AI-powered communication</li>
                              <li>Real-time comment threads</li>
                              <li>Revision tracking</li>
                              <li>Status notifications</li>
                              <li>One-click invoice conversion</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for quote management screens]
                      </>
                    )}
                    
                    {section.id === 'invoices' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Invoice Management</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Quote to invoice conversion</li>
                              <li>Custom branding and templates</li>
                              <li>Multi-currency support</li>
                              <li>Automated tax calculations</li>
                              <li>Batch invoice creation</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Payment Tracking</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Real-time payment status</li>
                              <li>Partial payment handling</li>
                              <li>Multiple payment methods</li>
                              <li>Automated reminders</li>
                              <li>Late payment management</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for invoice management screens]
                      </>
                    )}
                    
                    {section.id === 'payments' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Payment Processing</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Secure payment gateway integration</li>
                              <li>PCI compliance</li>
                              <li>Multiple payment method support</li>
                              <li>Transaction history tracking</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Reconciliation</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Automated payment matching</li>
                              <li>Manual reconciliation tools</li>
                              <li>Discrepancy reporting</li>
                              <li>Complete audit trail</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for payment processing screens]
                      </>
                    )}
                    
                    {section.id === 'expenses' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Receipt Management</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>AI-powered receipt scanning</li>
                              <li>Automatic categorization</li>
                              <li>Digital storage</li>
                              <li>Transaction matching</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Expense Features</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Custom categories</li>
                              <li>Recurring expense tracking</li>
                              <li>Approval workflows</li>
                              <li>Tax deduction tracking</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for expense tracking screens]
                      </>
                    )}
                    
                    {section.id === 'service-desk' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Ticket Management</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Multiple ticket types</li>
                              <li>Status tracking</li>
                              <li>Priority levels</li>
                              <li>Internal notes</li>
                              <li>Assignment system</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Admin Features</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Ticket overview dashboard</li>
                              <li>Advanced filtering</li>
                              <li>Bulk management</li>
                              <li>Performance metrics</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for service desk screens]
                      </>
                    )}
                    
                    {section.id === 'mobile-app' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Core Features</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Real-time dashboard</li>
                              <li>Document creation</li>
                              <li>Client management</li>
                              <li>Receipt scanning</li>
                              <li>Push notifications</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Offline Capabilities</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Offline data access</li>
                              <li>Background syncing</li>
                              <li>Conflict resolution</li>
                              <li>Document caching</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for mobile app screens]
                      </>
                    )}
                    
                    {section.id === 'ai-features' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Smart Communication</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>AI response suggestions</li>
                              <li>Context-aware recommendations</li>
                              <li>Professional tone maintenance</li>
                              <li>Multi-language support</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Business Intelligence</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Predictive analytics</li>
                              <li>Payment trend analysis</li>
                              <li>Client behavior insights</li>
                              <li>Smart recommendations</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for AI feature screens]
                      </>
                    )}
                    
                    {section.id === 'analytics' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Financial Analytics</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Revenue tracking</li>
                              <li>Cash flow analysis</li>
                              <li>Payment metrics</li>
                              <li>Tax reporting</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Business Metrics</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Quote conversion rates</li>
                              <li>Client engagement</li>
                              <li>Service performance</li>
                              <li>Custom reporting</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for analytics screens]
                      </>
                    )}
                    
                    {section.id === 'subscriptions' && (
                      <>
                        <div className="space-y-8">
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Free Tier</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Basic document management</li>
                              <li>Limited client management</li>
                              <li>Basic reporting</li>
                              <li>Email support</li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-3">Premium Features</h3>
                            <ul className="list-disc pl-6 space-y-2">
                              <li>Unlimited documents</li>
                              <li>Advanced client management</li>
                              <li>AI-powered features</li>
                              <li>Priority support</li>
                              <li>Custom branding</li>
                              <li>Advanced analytics</li>
                            </ul>
                          </div>
                        </div>
                        [PLACEHOLDER for subscription comparison screens]
                      </>
                    )}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation; 